// The completed-monthly API can return decimal amounts as numbers or strings.
// Keep absent or malformed values distinct from a real zero.
function amount(value) {
  if (typeof value === 'string') {
    const text = value.trim();
    if (!/^-?\d+(?:\.\d+)?$/.test(text)) return null;
    value = Number(text);
  }
  if (typeof value !== 'number' || !Number.isFinite(value) || Math.abs(value) > Number.MAX_SAFE_INTEGER) return null;
  return value;
}

const AMOUNT_KEYS = [
  ['grossAmount', 'gross_amount'],
  ['cancelAmount', 'cancel_amount'],
  ['netPaymentAmount', 'net_payment_amount'],
  ['feeAmount', 'fee_amount'],
  ['pgFeeAmount', 'pg_fee_amount'],
  ['adminFeeAmount', 'admin_fee_amount'],
  ['canceledFeeRefundAmount', 'canceled_fee_refund_amount'],
  ['carryIn', 'carry_in'],
  ['carryOut', 'carry_out'],
  ['netFeeAmount', 'net_fee_amount'],
  ['netAmount', 'net_amount'],
];

function readAmounts(source) {
  return Object.fromEntries(AMOUNT_KEYS.map(([key, alternate]) => [key, amount(source?.[key] ?? source?.[alternate])]));
}

/** data is the API's `data` payload, never a page-local `days` aggregate. */
export function getCompletedMonthlySummary(data) {
  const summary = data?.summary;
  const fullyClassified = typeof summary?.fullyClassified === 'boolean' ? summary.fullyClassified : null;
  const amounts = fullyClassified === true ? readAmounts(summary?.amounts) : readAmounts(null);
  return {
    transferCount: nonNegativeInteger(summary?.transferCount),
    transferAmount: amount(summary?.transferAmount),
    ...amounts,
    fullyClassified,
    classifiedAmounts: summary?.classifiedAmounts && typeof summary.classifiedAmounts === 'object' && !Array.isArray(summary.classifiedAmounts)
      ? readAmounts(summary.classifiedAmounts)
      : null,
  };
}

/** Parent settlement fields are deliberately not used as a payout-log fallback. */
export function getCompletedMonthlyRowAmounts(row) {
  const source = row?.settlementAmounts;
  const amounts = readAmounts(source);
  // Log rows provide the base values, while the server computes the derived
  // totals only for summary. Never treat an omitted component as zero.
  if (source?.netPaymentAmount == null && source?.net_payment_amount == null && amounts.grossAmount !== null && amounts.cancelAmount !== null) {
    amounts.netPaymentAmount = amount(amounts.grossAmount - amounts.cancelAmount);
  }
  if (source?.netFeeAmount == null && source?.net_fee_amount == null && amounts.feeAmount !== null && amounts.canceledFeeRefundAmount !== null) {
    amounts.netFeeAmount = amount(amounts.feeAmount - amounts.canceledFeeRefundAmount);
  }
  return {
    ...amounts,
    transferAmount: amount(row?.transfer_amount),
  };
}

function nonNegativeInteger(value) {
  if (value == null || value === '' || typeof value === 'boolean') return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
}

/** Unknown pagination is not evidence that the first page is the whole result. */
export function getCompletedMonthlyTotalPages(data, limit = 100) {
  const pagination = data?.pagination || {};
  const explicitPages = nonNegativeInteger(pagination.totalPages ?? pagination.total_pages ?? pagination.pageCount ?? pagination.page_count ?? data?.totalPages ?? data?.total_pages);
  if (explicitPages !== null) return Math.max(1, explicitPages);
  const total = nonNegativeInteger(pagination.total ?? data?.total);
  if (total === null) return null;
  const reportedLimit = nonNegativeInteger(pagination.limit ?? pagination.pageSize ?? pagination.page_size ?? data?.limit);
  const fallback = nonNegativeInteger(limit);
  const pageSize = Math.min(100, (reportedLimit && reportedLimit > 0 ? reportedLimit : fallback && fallback > 0 ? fallback : 100));
  return Math.max(1, Math.ceil(total / pageSize));
}

export function getCompletedMonthlyParams(monthFrom, monthTo, dateBasis, page) {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(String(monthFrom)) || !/^\d{4}-(0[1-9]|1[0-2])$/.test(String(monthTo)) || monthFrom > monthTo) {
    throw new Error('조회 기간을 확인해 주세요.');
  }
  if (!['DEPOSIT_DATE', 'PAYMENT_DATE'].includes(dateBasis)) throw new Error('조회 기준을 확인해 주세요.');
  if (!Number.isSafeInteger(page) || page < 1) throw new Error('조회 페이지를 확인해 주세요.');
  return { monthFrom, monthTo, dateBasis, page, limit: 100 };
}
